import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { usePasswordSignIn } from 'services/repositories/user/user';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { AuthRoutes, DashboardRoutes } from 'support/types';
import { defaultData } from 'pages/Auth/contexts/LoginStateContext/LoginStateContext';
import { useLoginForm } from 'pages/Auth/contexts/LoginStateContext/useLoginForm';
import { useLoginState } from 'pages/Auth/contexts/LoginStateContext/useLoginState';
import { Credentials } from 'pages/Auth/types';
import { Divider } from 'components/Divider/Divider';
import { Button } from 'components/Form/Button';
import { Input } from 'components/Form/Input';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';

export function PasswordLoginForm({ redirectTo = DashboardRoutes.root }: { redirectTo?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUrl: string = location.state?.from || redirectTo;

  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);
  const { switchToPasswordlessLogin } = useLoginState();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useLoginForm();
  const { mutate: passwordSigin, isLoading } = usePasswordSignIn();

  const onPasswordSignin = async (credentials: Credentials) => {
    setNonInputErrors([]);

    passwordSigin(credentials, {
      onSuccess: () => {
        navigate(redirectUrl, { replace: true });
      },

      onError: (err) => {
        processSubmissionErrors({ error: err, setInputError: setError, setNonInputErrors, defaultData, t });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onPasswordSignin)} className="space-y-4">
      <ValidationErrors errors={nonInputErrors} className="mb-6" />
      <Input
        {...register('email', { required: true })}
        placeholder={t('auth:email')}
        autoComplete="username"
        label={t('auth:emailAddress')}
        type="email"
        errors={errors.email}
        required
        readOnly={isLoading}
      />

      <Input
        {...register('password', { required: true })}
        placeholder={t('auth:password')}
        autoComplete="password"
        type="password"
        label={t('auth:password')}
        errors={errors.password}
        required
        readOnly={isLoading}
      />

      <Button type="submit" size="extra-large" fullWidth loading={isLoading} analyticsId="login:login">
        {t('auth:signIn')}
      </Button>

      <div className="flex w-full flex-col gap-8 pt-6">
        <Divider>
          <div className="absolute top-1/2 left-1/2 -translate-1/2 bg-white px-2 text-sm text-gray-500">
            {t('auth:otherOptions.heading')}
          </div>
        </Divider>

        <div className="flex w-full flex-col gap-4">
          <Button
            type="button"
            variant="text"
            className="text-gray-900! underline"
            fullWidth
            analyticsId="login:toggle-login-method"
            onClick={switchToPasswordlessLogin}
          >
            {t('auth:change-method.loginPasswordless')}
          </Button>

          <Link to={AuthRoutes.forgotPassword} className="w-full">
            <Button
              type="button"
              variant="text"
              className="text-gray-900! underline"
              fullWidth
              analyticsId="login:forgot-password"
            >
              {t('auth:forgotPassword')}
            </Button>
          </Link>
        </div>
      </div>
    </form>
  );
}
